import { formatShortJsDate } from "@/utils/DateFormatter";
import { areDatesOnSameDay } from "@/utils/DateHelper";
import {
  AppointmentType,
  ProjectStateInfo,
  ProjectFinishedInfo,
  ProjectStateType,
  ProjectApprovalDoneInfo,
  InsuranceOfferResultInfo,
  DryingResultInfo,
  DryingDurationStepInfo,
  AppointmentStepInfo,
  ReportInfo,
  DamageAssessmentDoneInfo,
  LeakDetectionDoneInfo,
  ResumedProjectStepInfo,
  SuspendedProjectStepInfo,
} from "@/data/models/ProjectState";
import { RepairAction } from "@/data/models/ProjectDetails";

export enum ColorOption {
  BLACK_TEXT,
  RED_TEXT,
  GREEN_TEXT,
  RED_BACKGROUND,
}

import { findUser } from "@/data/repository/UserRepository";

export interface ColoredText {
  text: string;
  color: ColorOption;
  leadingIcon?: string;
}

export interface LabelAndStatus {
  label: ColoredText;
  status?: ColoredText;
}

function getString(vue: Vue, res: string) {
  return vue.$t(res).toString();
}

function getColoredTextFromVue(
  vue: Vue,
  res: string,
  color?: ColorOption
): ColoredText {
  return {
    text: getString(vue, `projectStep.${res}`),
    color: color ?? ColorOption.BLACK_TEXT,
  };
}

function getMakeAppointmentStatus(vue: Vue) {
  return getColoredTextFromVue(vue, "makeAppointment", ColorOption.RED_TEXT);
}

function getMakeAppointmentStepDescription(vue: Vue, res: string) {
  return {
    label: getColoredTextFromVue(vue, res),
    status: getMakeAppointmentStatus(vue),
  };
}

function formatReportStep(vue: Vue, res: string, step: ReportInfo) {
  const label = getColoredTextFromVue(vue, res);

  const status = step.hasReport
    ? getColoredTextFromVue(vue, "reportCreated", ColorOption.GREEN_TEXT)
    : getColoredTextFromVue(vue, "noReport", ColorOption.RED_TEXT);

  return {
    label: label,
    status: status,
  };
}

export function formatEstimatedDryingDuration(
  vue: Vue,
  dryingDurationStepInfo: DryingDurationStepInfo
) {
  let color = ColorOption.BLACK_TEXT;

  if (dryingDurationStepInfo.isDryingEndOverdue) {
    color = ColorOption.RED_TEXT;
  }

  if (dryingDurationStepInfo.estimatedDryingDurationInDays) {
    return {
      text: vue
        .$tc(
          "projectStatus.formatDryingDuration",
          dryingDurationStepInfo.estimatedDryingDurationInDays
        )
        .toString(),
      color: color,
    };
  } else {
    return undefined;
  }
}

function getAppointmentDescription(vue: Vue, step: AppointmentStepInfo, showApartmentInfo = true) {
  let responsibleUser: string | null = null;
  let date: string | null = null;
  let apartment: string | null = null;

  let color: ColorOption = ColorOption.RED_TEXT;

  const appointmentDate = step.appointmentDate;

  if (appointmentDate) {
    date = formatShortJsDate(appointmentDate);

    const refDate = new Date();

    if (refDate.getTime() > appointmentDate.getTime()) {
      color = ColorOption.RED_BACKGROUND;
    } else {
      const isAppointmentToday = areDatesOnSameDay(refDate, appointmentDate);

      refDate.setDate(refDate.getDate() + 1);

      const isAppointmentTomorrow = areDatesOnSameDay(refDate, appointmentDate);

      if (isAppointmentToday || isAppointmentTomorrow) {
        color == ColorOption.RED_TEXT;
      } else {
        color = ColorOption.BLACK_TEXT;
      }
    }
  } else {
    date = vue.$t("projectStep.appointmentMissingDate").toString();
    color = ColorOption.RED_BACKGROUND;
  }

  if (step.responsibleUser) {
    responsibleUser =
      findUser(step.responsibleUser)?.nickname ?? step.responsibleUser;
  } else {
    responsibleUser = vue
      .$t("projectStep.appointmentMissingResponsibleUser")
      .toString();
    color = ColorOption.RED_BACKGROUND;
  }

  if (step.contactPersonNumber && showApartmentInfo) {
    apartment = `(${vue
      .$t("clientData.apartmentNumber", [step.contactPersonNumber])
      .toString()})`;
  }

  const appointmentDescription = (step.appointmentType ==
  AppointmentType.FOLLOW_UP_INSPECTION
    ? [vue.$t("projectStep.followUpInspectionShorthand").toString(), date]
    : [responsibleUser, date, apartment]
  )
    .filter((text) => !!text)
    .join(" ");

  const showAppointmentDescription =
    appointmentDescription && appointmentDescription.length > 0;

  return showAppointmentDescription
    ? {
        text: appointmentDescription,
        color: color,
      }
    : undefined;
}

export function formatProjectStepInfo(
  vue: Vue,
  projectStepInfo: ProjectStateInfo,
  showApartmentInfo = true,
  hasDrying = true
): LabelAndStatus | null {
  const projectStepType = projectStepInfo.projectStepType;
  const projectApprovalDoneInfo = projectStepInfo as ProjectApprovalDoneInfo;
  const wasApproved = projectApprovalDoneInfo.wasApproved ?? null;

  const getColoredText: (
    stringRes: string,
    color?: ColorOption
  ) => ColoredText = (stringRes, color) =>
    getColoredTextFromVue(vue, stringRes, color);

  if (wasApproved === false) {
    return {
      label: getColoredText("projectFinished"),
      status: getColoredText("orderRejected", ColorOption.GREEN_TEXT)
    };
  }

  switch (projectStepType) {
    case ProjectStateType.ConcludeProject: {
      return {
        label: getColoredText("dryingFinished"),
        status: getColoredText("concludeProject", ColorOption.RED_TEXT),
      };
    }
    case ProjectStateType.SetProjectSupervisor: {
      return {
        label: getColoredText("projectApproved"),
        status: getColoredText("setProjectSupervisor", ColorOption.RED_TEXT),
      };
    }
    case ProjectStateType.AssignDevices: {
      return {
        label: getColoredText("dryingApproved"),
        status: getColoredText("assignDevices", ColorOption.RED_TEXT),
      };
    }
    case ProjectStateType.ApproveProject: {
      return {
        label: getColoredText("orderRecorded"),
        status: getColoredText("approveProject", ColorOption.RED_TEXT),
      };
    }
    case ProjectStateType.ProjectApprovalDone: {
      const projectApprovalDoneInfo = projectStepInfo as ProjectApprovalDoneInfo;

      if (projectApprovalDoneInfo.wasApproved) {
        return {
          label: getColoredText("projectApproved"),
          status: getColoredText("establishFirstContact", ColorOption.RED_TEXT),
        };
      } else {
        return {
          label: getColoredText("projectFinished"),
          status: getColoredText("orderRejected", ColorOption.GREEN_TEXT),
        };
      }
    }
    case ProjectStateType.FirstContactDone:
      return getMakeAppointmentStepDescription(vue, "firstContactEstablished");
    case ProjectStateType.LeakDetectionRunning:
      return {
        label: getColoredText("leakDetectionRunning"),
      };
    case ProjectStateType.LeakDetectionFinished: {
      const leakDetectionDoneInfo = projectStepInfo as LeakDetectionDoneInfo;
      return formatReportStep(
        vue,
        "leakDetectionFinished",
        leakDetectionDoneInfo
      );
    }
    case ProjectStateType.DamageAssessmentRunning: {
      return {
        label: getColoredText("damageAssessmentRunning"),
      };
    }
    case ProjectStateType.DamageAssessmentDone: {
      const damageAssessmentDoneInfo = projectStepInfo as DamageAssessmentDoneInfo;
      const label = getColoredText("damageAssessmentFinished");

      let status: ColoredText | undefined = undefined;

      if (!damageAssessmentDoneInfo.hasReport) {
        status = getColoredText("noReport", ColorOption.RED_TEXT);
      } else if (damageAssessmentDoneInfo.isProjectOnHold) {
        status = getColoredText("projectOnHold", ColorOption.RED_TEXT);
      } else if (damageAssessmentDoneInfo.noDryingNecessary) {
        status = getColoredText(hasDrying ? "noDrying" : "noRestoration", ColorOption.GREEN_TEXT);
      }

      return {
        label: label,
        status: status,
      };
    }
    case ProjectStateType.DryingApproved: {
      return getMakeAppointmentStepDescription(vue, hasDrying ? "dryingApproved" : "restorationApproved");
    }
    case ProjectStateType.InsuranceOfferSend: {
      return {
        label: getColoredText("offerSend"),
      };
    }
    case ProjectStateType.InsuranceOfferResult: {
      const insuranceOfferResultInfo = projectStepInfo as InsuranceOfferResultInfo;

      if (insuranceOfferResultInfo.dryingApproved) {
        return getMakeAppointmentStepDescription(vue, hasDrying ? "dryingApproved" : "restorationApproved");
      } else if (insuranceOfferResultInfo.projectFinished) {
        return {
          label: getColoredText("projectFinished"),
          status: getColoredText("noContract", ColorOption.GREEN_TEXT),
        };
      } else {
        return null;
      }
    }
    case ProjectStateType.DryingPreperationRunning: {
      const dryingDurationStepInfo = projectStepInfo as DryingDurationStepInfo;

      return {
        label: getColoredText("projectStepSetupDrying"),
        status: formatEstimatedDryingDuration(vue, dryingDurationStepInfo),
      };
    }
    case ProjectStateType.DryingStarted: {
      const dryingDurationStepInfo = projectStepInfo as DryingDurationStepInfo;

      return {
        label:
          dryingDurationStepInfo.numDryingDays === undefined
            ? getColoredText("dryingRunning")
            : {
                text: vue
                  .$t(`projectStep.formatDryingRunningSince`, [
                    vue.$tc(
                      "common.duration.formatDaysOpenEnded",
                      dryingDurationStepInfo.numDryingDays
                    ),
                  ])
                  .toString(),
                color: ColorOption.BLACK_TEXT,
              },
        status: formatEstimatedDryingDuration(vue, dryingDurationStepInfo),
      };
    }
    case ProjectStateType.DryingResult: {
      const dryingResultInfo = projectStepInfo as DryingResultInfo;

      let status: ColoredText | undefined = undefined;

      switch (dryingResultInfo.repairAction) {
        case RepairAction.IMMEDIATELY:
          status = getColoredText("projectStepRepairStarted");
          break;
        case RepairAction.APPOINTMENT:
          status = getColoredText("makeAppointment", ColorOption.RED_TEXT);
          break;
        default:
          if (dryingResultInfo.hasReport) {
            status = getColoredTextFromVue(
              vue,
              "reportCreated",
              ColorOption.GREEN_TEXT
            );
          }
      }

      return {
        label: getColoredText("dryingConcluded"),
        status: status,
      };
    }
    case ProjectStateType.Appointment: {
      const appointmentStepInfo = projectStepInfo as AppointmentStepInfo;

      let label: ColoredText | undefined = undefined;

      switch (appointmentStepInfo.appointmentType) {
        case AppointmentType.REPAIR: {
          label = {
            color: ColorOption.BLACK_TEXT,
            text: vue.$t("projectStatus.appointmentStep16").toString(),
          };
          break;
        }
        case AppointmentType.LEAK_DETECTION: {
          label = {
            color: ColorOption.BLACK_TEXT,
            text: vue.$t("projectStatus.appointmentStep4").toString(),
          };
          break;
        }
        case AppointmentType.DAMAGE_ASSESSMENT: {
          label = {
            color: ColorOption.BLACK_TEXT,
            text: vue.$t("projectStatus.appointmentStep6").toString(),
          };
          break;
        }
        case AppointmentType.PREPARE_DRYING: {
          label = {
            color: ColorOption.BLACK_TEXT,
            text: vue.$t(`projectStatus.appointmentStep10`).toString(),
          };
          break;
        }
        case AppointmentType.INTERMEDIATE_MEASUREMENT: {
          label = {
            color: ColorOption.BLACK_TEXT,
            text: vue.$t("projectStatus.appointmentStep12").toString(),
          };
          break;
        }
        case AppointmentType.DRYING_CONCLUSION: {
          label = {
            color: ColorOption.BLACK_TEXT,
            text: vue.$t(`projectStatus.appointmentStep14`).toString(),
          };
          break;
        }
        case AppointmentType.FOLLOW_UP_INSPECTION: {
          label = getColoredText("dryingRunning");
          break;
        }
      }

      return {
        label: label,
        status: getAppointmentDescription(vue, appointmentStepInfo, showApartmentInfo),
      };
    }
    case ProjectStateType.RepairStarted: {
      return {
        label: getColoredText("projectStepRepairStarted"),
      };
    }
    case ProjectStateType.RepairCompleted: {
      return {
        label: getColoredText("projectStepRepairFinished"),
      };
    }
    case ProjectStateType.PhaseSuspended: {
      const suspendedProjectStepInfo = projectStepInfo as SuspendedProjectStepInfo;
      return {
        label: getColoredText(suspendedProjectStepInfo.suspendedResourceKey),
      };
    }
    case ProjectStateType.PhaseResumed: {
      const resumedProjectStepInfo = projectStepInfo as ResumedProjectStepInfo;
      return {
        label: getColoredText(resumedProjectStepInfo.resumedResourceKey),
      };
    }
    case ProjectStateType.ProjectFinished: {
      return formatReportStep(
        vue,
        "projectFinished",
        projectStepInfo as ProjectFinishedInfo
      );
    }
  }
}
